import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft, Edit2, UserX, UserCheck, Save, X,
  Loader2, Shield, Activity, Settings, Users, BarChart3,
} from 'lucide-react'
import { PageHeader, fadeUp } from '@personic/shared'
import { useAuthStore } from '@personic/shared'
import {
  useAdminUser,
  useUpdateUser,
  useDeactivateUser,
  useActivateUser,
  useUserFeatures,
  useGrantFeatures,
  useRevokeFeature,
  type AdminUser,
} from '@/hooks/useAdminUsers'
import { useAuditLog } from '@/hooks/useAuditLog'
import { useAdminTenant } from '@/hooks/useAdminTenants'
import UsageTab from '@/components/admin/UsageTab'
import { formatDistanceToNow } from 'date-fns'

// =============================================================================
// ROLE BADGE (reused from UserDirectory)
// =============================================================================

const ROLE_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  superuser:    { label: 'Superuser',    bg: 'bg-amber-50',   text: 'text-amber-700',  dot: 'bg-amber-500' },
  admin:        { label: 'Admin',        bg: 'bg-purple-50',  text: 'text-purple-700', dot: 'bg-purple-500' },
  employee:     { label: 'Employee',     bg: 'bg-sky-50',     text: 'text-sky-700',    dot: 'bg-sky-500' },
  tenant_admin: { label: 'Tenant Admin', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  consumer:     { label: 'Consumer',     bg: 'bg-teal-50',    text: 'text-teal-700',   dot: 'bg-teal-500' },
}

function RoleBadge({ role }: { role: string }) {
  const config = ROLE_CONFIG[role] || { label: role, bg: 'bg-warm-50', text: 'text-warm-700', dot: 'bg-warm-500' }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  )
}

// =============================================================================
// TAB DEFINITIONS
// =============================================================================

type TabId = 'profile' | 'features' | 'usage' | 'activity' | 'tenant'

interface TabDef {
  id: TabId
  label: string
  icon: React.ElementType
  showForRoles?: string[]
}

const TABS: TabDef[] = [
  { id: 'profile', label: 'Profile', icon: Settings },
  { id: 'features', label: 'Products & Features', icon: Shield },
  { id: 'usage', label: 'Usage', icon: BarChart3 },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'tenant', label: 'Tenant', icon: Users, showForRoles: ['tenant_admin'] },
]

// =============================================================================
// PROFILE TAB
// =============================================================================

function ProfileTab({ user, currentUser }: { user: AdminUser; currentUser: any }) {
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState({ name: user.name })
  const updateUser = useUpdateUser()

  const canEdit = currentUser?.role === 'superuser' || currentUser?.role === 'admin'

  const handleSave = async () => {
    try {
      await updateUser.mutateAsync({ userId: user.id, data: { name: editForm.name } })
      setEditing(false)
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-heading font-semibold text-midnight-800">User Information</h3>
          {canEdit && !editing && (
            <button onClick={() => { setEditing(true); setEditForm({ name: user.name }) }} className="btn btn-ghost text-sm inline-flex items-center gap-1.5">
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="label">Name</label>
            {editing ? (
              <input
                type="text"
                value={editForm.name}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                className="input w-full"
              />
            ) : (
              <p className="text-sm text-midnight-800">{user.name}</p>
            )}
          </div>
          <div>
            <label className="label">Email</label>
            <p className="text-sm text-midnight-800">{user.email}</p>
          </div>
          <div>
            <label className="label">Role</label>
            <div className="mt-0.5"><RoleBadge role={user.role} /></div>
          </div>
          <div>
            <label className="label">Status</label>
            <p className="text-sm">
              {user.is_active ? (
                <span className="text-green-600 font-medium">Active</span>
              ) : (
                <span className="text-warm-400 font-medium">Inactive</span>
              )}
            </p>
          </div>
          <div>
            <label className="label">MFA</label>
            <p className="text-sm text-midnight-800">{user.mfa_enabled ? 'Enabled' : 'Disabled'}</p>
          </div>
          <div>
            <label className="label">Created</label>
            <p className="text-sm text-warm-600">
              {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '\u2014'}
            </p>
          </div>
          <div>
            <label className="label">Last Login</label>
            <p className="text-sm text-warm-600">
              {user.last_login ? formatDistanceToNow(new Date(user.last_login), { addSuffix: true }) : 'Never'}
            </p>
          </div>
          {user.tenant_name && (
            <div>
              <label className="label">Tenant</label>
              <p className="text-sm text-midnight-800">{user.tenant_name} <span className="text-warm-400">({user.tenant_slug})</span></p>
            </div>
          )}
        </div>

        {editing && (
          <div className="flex items-center gap-2 mt-5 pt-4 border-t border-warm-200">
            <button onClick={handleSave} disabled={updateUser.isPending} className="btn btn-primary text-sm inline-flex items-center gap-1.5">
              {updateUser.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
            <button onClick={() => setEditing(false)} className="btn btn-ghost text-sm inline-flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// FEATURES TAB
// =============================================================================

function FeaturesTab({ user }: { user: AdminUser }) {
  const { data: features, isLoading } = useUserFeatures(user.id)
  const grantFeatures = useGrantFeatures()
  const revokeFeature = useRevokeFeature()

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-warm-400 animate-spin" /></div>
  }

  // Group features by category
  const grouped = (features || []).reduce<Record<string, typeof features>>((acc, f) => {
    const cat = f.category || 'other'
    if (!acc[cat]) acc[cat] = []
    acc[cat]!.push(f)
    return acc
  }, {})

  const categoryLabels: Record<string, string> = {
    product: 'Product Access',
    admin: 'Admin Features',
    module: 'Module Access',
    distillation: 'Distillation Signals',
    portal: 'Portal Features',
    limit: 'Usage Limits',
  }

  const handleToggle = async (featureKey: string, hasAccess: boolean, source: string) => {
    if (source === 'role') return // Can't toggle role-based features
    if (hasAccess) {
      await revokeFeature.mutateAsync({ userId: user.id, featureKey })
    } else {
      await grantFeatures.mutateAsync({ userId: user.id, features: [featureKey] })
    }
  }

  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([category, feats]) => (
        <div key={category} className="card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-warm-200 bg-warm-50/40">
            <h4 className="text-sm font-semibold text-midnight-800">
              {categoryLabels[category] || category}
            </h4>
          </div>
          <div className="divide-y divide-warm-100">
            {feats!.map(feature => (
              <div key={feature.key} className="flex items-center justify-between px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-midnight-800">{feature.name}</p>
                  <p className="text-xs text-warm-500 mt-0.5 truncate">{feature.description}</p>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    feature.source === 'role' ? 'bg-purple-50 text-purple-600' :
                    feature.source === 'override' ? 'bg-copper-50 text-copper-600' :
                    feature.source === 'default' ? 'bg-warm-100 text-warm-500' :
                    'bg-warm-50 text-warm-400'
                  }`}>
                    {feature.source}
                  </span>
                  <button
                    onClick={() => handleToggle(feature.key, feature.has_access, feature.source)}
                    disabled={feature.source === 'role'}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      feature.has_access ? 'bg-copper-500' : 'bg-warm-300'
                    } ${feature.source === 'role' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      feature.has_access ? 'translate-x-4.5 left-0' : 'left-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// ACTIVITY TAB
// =============================================================================

function ActivityTab({ user }: { user: AdminUser }) {
  const { data: auditData, isLoading } = useAuditLog({ limit: 50 })

  // Filter entries related to this user (as actor or target)
  const entries = (auditData?.entries || []).filter(
    (e: any) => e.actor_user_id === user.id || e.target_user_id === user.id
  )

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-warm-400 animate-spin" /></div>
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-3.5 border-b border-warm-200 bg-warm-50/40">
        <h4 className="text-sm font-semibold text-midnight-800">Audit Log</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-warm-100">
          <thead className="bg-warm-50/60">
            <tr>
              {['Timestamp', 'Action', 'Actor', 'Target', 'Details'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-warm-100">
            {entries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-sm text-warm-400">No activity found for this user.</td>
              </tr>
            ) : (
              entries.map((entry: any) => (
                <tr key={entry.id}>
                  <td className="px-5 py-3 text-sm text-warm-600 whitespace-nowrap">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '\u2014'}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-warm-100 text-warm-700">
                      {entry.action?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-warm-700">{entry.actor_name || entry.actor_email || '\u2014'}</td>
                  <td className="px-5 py-3 text-sm text-warm-700">{entry.target_user_name || entry.target_user_email || '\u2014'}</td>
                  <td className="px-5 py-3 text-sm text-warm-500 truncate max-w-[200px]">
                    {entry.feature_name || (entry.details ? JSON.stringify(entry.details).substring(0, 60) : '\u2014')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// =============================================================================
// TENANT TAB
// =============================================================================

function TenantTab({ user }: { user: AdminUser }) {
  const { data: tenant, isLoading } = useAdminTenant(user.tenant_id || '')

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-warm-400 animate-spin" /></div>
  }

  if (!tenant) {
    return <div className="text-center py-12 text-warm-400">No tenant associated with this user.</div>
  }

  const statusColors: Record<string, string> = {
    active: 'bg-green-50 text-green-700',
    pending: 'bg-amber-50 text-amber-700',
    suspended: 'bg-red-50 text-red-700',
    cancelled: 'bg-warm-100 text-warm-500',
  }

  return (
    <div className="card p-6">
      <h3 className="text-base font-heading font-semibold text-midnight-800 mb-5">Tenant Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="label">Name</label>
          <p className="text-sm text-midnight-800">{tenant.name}</p>
        </div>
        <div>
          <label className="label">Slug</label>
          <p className="text-sm text-warm-600 font-mono">{tenant.slug}</p>
        </div>
        <div>
          <label className="label">Plan</label>
          <p className="text-sm text-midnight-800 capitalize">{tenant.plan}</p>
        </div>
        <div>
          <label className="label">Status</label>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[tenant.status] || 'bg-warm-100 text-warm-500'}`}>
            {tenant.status}
          </span>
        </div>
        <div>
          <label className="label">Created</label>
          <p className="text-sm text-warm-600">
            {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '\u2014'}
          </p>
        </div>
        <div>
          <label className="label">User Count</label>
          <p className="text-sm text-midnight-800">{tenant.user_count ?? '\u2014'}</p>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// MAIN USER DETAIL PAGE
// =============================================================================

export default function UserDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()
  const { data: user, isLoading } = useAdminUser(id || null)
  const deactivateUser = useDeactivateUser()
  const activateUser = useActivateUser()

  const [activeTab, setActiveTab] = useState<TabId>('profile')

  const canManage = currentUser?.role === 'superuser' || currentUser?.role === 'admin'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-copper-500 animate-spin" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-24">
        <p className="text-warm-500 text-lg">User not found</p>
        <button onClick={() => navigate('/admin/users')} className="btn btn-ghost mt-4">
          Back to Directory
        </button>
      </div>
    )
  }

  const breadcrumbs = [
    { label: 'System Admin', href: '/admin' },
    { label: 'User Management', href: '/admin/users' },
    { label: user.name },
  ]

  const visibleTabs = TABS.filter(tab => {
    if (!tab.showForRoles) return true
    return tab.showForRoles.includes(user.role)
  })

  const handleToggleActive = async () => {
    if (user.is_active) {
      await deactivateUser.mutateAsync(user.id)
    } else {
      await activateUser.mutateAsync(user.id)
    }
  }

  return (
    <div>
      <PageHeader title="" breadcrumbs={breadcrumbs} />

      <motion.div variants={fadeUp} initial="hidden" animate="visible">
        {/* User Header */}
        <div className="card p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/users')} className="p-2 -ml-2 rounded-lg hover:bg-warm-50 transition-colors">
                <ArrowLeft className="w-5 h-5 text-warm-500" />
              </button>
              <div className="w-14 h-14 rounded-full bg-copper-100 flex items-center justify-center">
                <span className="text-copper-600 font-bold text-xl">
                  {user.name?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-midnight-800">{user.name}</h2>
                <p className="text-sm text-warm-500">{user.email}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <RoleBadge role={user.role} />
                  {user.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-600">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-warm-100 text-warm-400">
                      Inactive
                    </span>
                  )}
                </div>
              </div>
            </div>

            {canManage && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleToggleActive}
                  disabled={deactivateUser.isPending || activateUser.isPending}
                  className={`btn text-sm inline-flex items-center gap-1.5 ${
                    user.is_active
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                  }`}
                >
                  {(deactivateUser.isPending || activateUser.isPending) ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : user.is_active ? (
                    <UserX className="w-3.5 h-3.5" />
                  ) : (
                    <UserCheck className="w-3.5 h-3.5" />
                  )}
                  {user.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-warm-200 mb-6">
          <nav className="-mb-px flex space-x-6">
            {visibleTabs.map(tab => {
              const Icon = tab.icon
              const active = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                    active
                      ? 'border-copper-500 text-copper-700'
                      : 'border-transparent text-warm-500 hover:text-midnight-700 hover:border-warm-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'profile' && <ProfileTab user={user} currentUser={currentUser} />}
          {activeTab === 'features' && <FeaturesTab user={user} />}
          {activeTab === 'usage' && <UsageTab user={user} />}
          {activeTab === 'activity' && <ActivityTab user={user} />}
          {activeTab === 'tenant' && <TenantTab user={user} />}
        </motion.div>
      </motion.div>
    </div>
  )
}
